import React from 'react'
import {
  VStack,
  Heading,
  Center,
  Text,
  Stack,
  Grid,
  GridItem,
  Box,
  Button,
  useColorModeValue,
} from '@chakra-ui/react'
import { BsCircle } from 'react-icons/bs'
import { ImCross } from 'react-icons/im'
import type { Quiz, Stats } from 'types/generated/graphql'
import { css } from '@emotion/react'
import { LayoutEmbedQuizPlaylist } from '../Common/LayoutEmbedQuizPlaylist'

type Props = {
  quiz: Quiz & { answerCandidates: string[] }
  quizStats?: Stats
  answer: string
  onNext: () => void
}

const correctText = css`
  -webkit-text-stroke: 0.5px white;
  -webkit-text-fill-color: red;
`
const incorrectText = css`
  -webkit-text-stroke: 0.5px white;
  -webkit-text-fill-color: #4169e1;
`

export const EmbedAnswered: React.FC<Props> = ({
  quiz,
  quizStats,
  answer,
  onNext,
}) => {
  const buttonNextColor = useColorModeValue('buttonNextLight', 'buttonNextDark')
  // 非埋め込みの場合は問題文と解説両方表示できるが、
  // 埋め込みの場合は、解説があれば解説、なければ問題文を表示
  const displayText = () => {
    if (quiz.content.description) {
      return `解説：${quiz.content.description}`
    }
    const stats = quizStats ? `(正答率${quizStats.stats[0].percent})` : ''
    return `Q：${quiz.content.question}${stats}`
  }

  return (
    <LayoutEmbedQuizPlaylist
      imageUrl={quiz.content.answerImageUrl!}
      alt={quiz.content.question}
    >
      <VStack w="100%" spacing={3}>
        <Heading
          css={answer === quiz.content.correct ? correctText : incorrectText}
          as="h3"
          size="lg"
          mb={{ md: '0.5rem' }}
        >
          {answer === quiz.content.correct ? '正解' : '不正解'}
        </Heading>
        <Text mb="1rem" color="white">
          {displayText()}
        </Text>
        <Stack w="100%" mb="2rem" spacing={4} direction="row" align="center">
          <Grid
            w="100%"
            templateRows={{ base: 'repeat(1, 1fr)', md: 'repeat(1, 1fr)' }}
            templateColumns={{
              base: 'repeat(1, 1fr)',
              md:
                answer === quiz.content.correct
                  ? 'repeat(2, 1fr)'
                  : 'repeat(4, 1fr)',
            }}
            gap={4}
          >
            {quiz.answerCandidates.map((candidate, index) => {
              // 未選択かつ不正解の選択肢は表示しない
              if (candidate !== quiz.content.correct && answer !== candidate) {
                return null
              }
              return (
                <GridItem
                  w="100%"
                  rowSpan={{ base: 1, md: 1 }}
                  colSpan={{ base: 1, md: 2 }}
                  colorscheme="teal"
                  key={'quizPlaylistAnswer-' + index}
                >
                  <Box
                    boxShadow="base"
                    p="3"
                    rounded="md"
                    bg="whiteAlpha.800"
                    w="100%"
                  >
                    {(() => {
                      // 正解
                      if (candidate === quiz.content.correct) {
                        return (
                          <Center color="red">
                            <BsCircle />：
                            <Text fontWeight="bold">{candidate}</Text>
                          </Center>
                        )
                      }

                      // 間違い
                      if (answer === candidate) {
                        return (
                          <Center color="#4169e1">
                            <ImCross />：{candidate}
                          </Center>
                        )
                      }
                    })()}
                  </Box>
                </GridItem>
              )
            })}
          </Grid>
        </Stack>

        <Button
          bg={buttonNextColor}
          color="blackAlpha.800"
          w="200px"
          mb={3}
          onClick={onNext}
        >
          次へ
        </Button>
      </VStack>
    </LayoutEmbedQuizPlaylist>
  )
}
